'use client';

import { c as _c } from "react/compiler-runtime";
import { TZDate } from '@date-fns/tz';
import { formatIsoDate } from '@utils/utils-format';
import { addDays } from 'date-fns';
import { useMemo } from 'react';
import { DEFAULT_TIME_ZONE } from '@/lib/time-zone';
import { useExperienceFilterContext } from '../_components/app-experience-filter-provider';
import type { Experience } from '../_lib/experience-state-types';
import { getTicketRequestedRange, type PurchasableDay, projectPurchasableDays, ticketCalendarHorizon } from '../_lib/ticket-calendar';
import { useAppExperienceOpeningHours } from './use-app-experience-opening-hours';
import { useExperiencePreviewToken } from './use-experience-preview-token';
export type { PurchasableDay } from '../_lib/ticket-calendar';
export { TICKET_CALENDAR_HORIZON, TICKET_DAYS_PREVIEW_COUNT } from '../_lib/ticket-calendar';
function useSharedTicketOpeningHours(experience, enabled) {
  const {
    ticketCalendar
  } = useExperienceFilterContext();
  const previewToken = useExperiencePreviewToken();
  return useAppExperienceOpeningHours(experience.publicId, ticketCalendar.from, ticketCalendar.to, enabled, previewToken);
}
export function useAppExperiencePurchasableDays(experience: Experience, enabled = true) {
  const {
    ticketCalendar,
    filter
  } = useExperienceFilterContext();
  const calendar = useSharedTicketOpeningHours(experience, enabled);
  const previewToken = useExperiencePreviewToken();
  const range = getTicketRequestedRange(ticketCalendar, filter);
  // Explicit dates beyond the normal browsing horizon get their own bounded read.
  const requested = useAppExperienceOpeningHours(experience.publicId, range?.from ?? '', range?.to ?? '', enabled && !!range, previewToken);
  const purchasableDays = useMemo<PurchasableDay[]>(() => {
    const byDate = new Map([...calendar.openingHours, ...(range ? requested.openingHours : [])].map(day => [day.date, day]));
    return projectPurchasableDays([...byDate.values()], ticketCalendar.today, ticketCalendar.offerEnd, ticketCalendar.offerStart).toSorted((a, b) => a.date.localeCompare(b.date));
  }, [calendar.openingHours, requested.openingHours, range, ticketCalendar]);
  return {
    purchasableDays,
    isLoading: calendar.isLoading || !!range && requested.isLoading,
    isError: calendar.isError || !!range && requested.isError
  };
}
export function useAppExperienceOpeningHoursWeek(experience, t0) {
  const $ = _c(13);
  const enabled = t0 === undefined ? true : t0;
  const {
    ticketCalendar
  } = useExperienceFilterContext();
  const {
    openingHours,
    isLoading,
    isError
  } = useSharedTicketOpeningHours(experience, enabled);
  let t1;
  if ($[0] !== ticketCalendar.from) {
    const monday = new TZDate(ticketCalendar.from, DEFAULT_TIME_ZONE);
    t1 = formatIsoDate(addDays(monday, 6));
    $[0] = ticketCalendar.from;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const last = t1;
  let t2;
  if ($[2] !== last || $[3] !== openingHours || $[4] !== ticketCalendar.from) {
    let t3;
    if ($[6] !== last || $[7] !== ticketCalendar.from) {
      t3 = day => day.date >= ticketCalendar.from && day.date <= last;
      $[6] = last;
      $[7] = ticketCalendar.from;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    t2 = openingHours.filter(t3);
    $[2] = last;
    $[3] = openingHours;
    $[4] = ticketCalendar.from;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const weekDays = t2;
  let t3;
  if ($[9] !== isError || $[10] !== isLoading || $[11] !== weekDays) {
    t3 = {
      weekDays,
      isLoading,
      isError
    };
    $[9] = isError;
    $[10] = isLoading;
    $[11] = weekDays;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
}
export function useAppExperienceVisitDay(publicId: number, visitDate?: string | null) {
  const {
    openingHours,
    isLoading
  } = useAppExperienceOpeningHours(publicId, visitDate ?? '', visitDate ?? '', !!visitDate);
  const visitDay = useMemo<PurchasableDay | null>(() => {
    const [day] = openingHours;
    if (!day || day.closed) return null;
    return {
      date: day.date,
      weekday: day.weekday,
      windows: day.windows
    };
  }, [openingHours]);
  return {
    visitDay,
    isLoading
  };
}
export function useTicketCalendarHorizon(_experience) {
  const $ = _c(2);
  const {
    ticketCalendar
  } = useExperienceFilterContext();
  let t0;
  if ($[0] !== ticketCalendar) {
    t0 = ticketCalendarHorizon(ticketCalendar);
    $[0] = ticketCalendar;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}